import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldsetFormList, FieldsetFormListItem } from "./FieldsetFormList";
import {
  createFieldset,
  deleteFieldset,
  updateFieldset,
} from "../../../api/fieldsetApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../utils/store";
import { Fieldset, ModifiedFieldset } from "./types";
import { Section } from "../../../planner/document-structure/types";
import {
  Button,
  SearchBar,
  ToggleCheckbox,
} from "../../../components/tailwind";
const OFFSET = 360;

const SUBMIT_BUTTON_NAMES = {
  CREATE: "Create",
  UPDATE: "Update",
  COPY: "Copy",
} as const;

type Props = {
  knownKeys: string[];
  loading?: boolean;
  refetchFieldsets: () => Promise<void>;
  selectedFieldset: Fieldset | undefined;
  setSelectedFieldset: (fieldset: Fieldset) => void;
  isCopying?: boolean;
  setIsCopying?: Dispatch<SetStateAction<boolean>>;
  sections: Section[];
  isFetchingSections: boolean;
};

const HEADING_CLASSES = "tw-text-base tw-font-bold tw-text-gray-900";

export const FieldsetForm: React.FC<Props> = ({
  knownKeys,
  loading,
  refetchFieldsets,
  selectedFieldset,
  setSelectedFieldset,
  isCopying,
  setIsCopying,
  sections,
  isFetchingSections,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [selectedItems, setSelectedItems] = useState<FieldsetFormListItem[]>(
    []
  );
  const [fieldsetName, setFieldsetName] = useState<string>("");
  const [useAsDefault, setUseAsDefault] = useState<boolean>(
    selectedFieldset?.use_as_default || false
  );
  const [searchString, setSearchString] = useState<string>("");
  const [missingName, setMissingName] = useState<boolean>(false);
  const [missingKeys, setMissingKeys] = useState<boolean>(false);
  const [showSelectedFields, setShowSelectedFields] = useState(false);
  const [showSelectedSections, setShowSelectedSections] = useState(false);

  const allowedSections = useMemo(
    () => sections.filter(({ template_label_ids }) => !template_label_ids),
    [sections]
  );

  const isUnsavedDefaultFieldset = useMemo(
    () => selectedFieldset?.use_as_default && selectedFieldset?.id === 0,
    [selectedFieldset]
  );

  const submitButtonName = useMemo(() => {
    if (!selectedFieldset) return SUBMIT_BUTTON_NAMES.CREATE;
    return isCopying ? SUBMIT_BUTTON_NAMES.COPY : SUBMIT_BUTTON_NAMES.UPDATE;
  }, [selectedFieldset, isCopying]);

  // Track the last fieldset id we synced the form to. We only clear the form
  // when the user explicitly switches schemas; later refetches of
  // sections/knownKeys must not wipe a name the user is typing in create mode.
  const lastSyncedIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const currentId = selectedFieldset?.id ?? null;
    const idChanged = lastSyncedIdRef.current !== currentId;
    lastSyncedIdRef.current = currentId;

    if (!selectedFieldset) {
      if (!idChanged) return;
      setSelectedItems([]);
      setFieldsetName("");
      setUseAsDefault(false);
      setMissingName(false);
      setMissingKeys(false);
      return;
    }

    setUseAsDefault(selectedFieldset.use_as_default);
    if (selectedFieldset.use_as_default && selectedFieldset.fields.length < 1) {
      setSelectedItems(knownKeys.map((key) => ({ name: key, origin: "data" })));
    } else {
      if (isFetchingSections) return;
      const next: FieldsetFormListItem[] = [];
      selectedFieldset.fields.forEach((field) => {
        if (field?.section_id) {
          next.push({
            name:
              allowedSections.find(({ id }) => id === field.section_id)?.name ||
              "Unknown section name",
            id: field.section_id,
            origin: "section",
          });
        } else if (field?.key) {
          next.push({
            name: field.key,
            origin: "data",
          });
        }
      });
      setSelectedItems(next);
    }
    let name = selectedFieldset.name;
    if (isCopying) {
      setUseAsDefault(false);
      name += "-copy";
    }
    setFieldsetName(name);
    setMissingName(false);
    setMissingKeys(false);
  }, [
    selectedFieldset,
    isCopying,
    isFetchingSections,
    allowedSections,
    knownKeys,
  ]);

  const [height, setHeight] = useState<number>(0);
  const handleWindowResize = useCallback(() => {
    const windowHeight = document.documentElement.clientHeight;
    const windowScrollHeight = document.documentElement.scrollHeight;
    const scrollOffset = windowScrollHeight - windowHeight;
    setHeight(Math.max(windowHeight - scrollOffset - OFFSET, 200));
  }, []);

  useLayoutEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [handleWindowResize]);

  const selectedFieldNames = useMemo(
    () =>
      new Set(
        selectedItems.filter((i) => i.origin === "data").map((i) => i.name)
      ),
    [selectedItems]
  );
  const selectedSectionIds = useMemo(
    () =>
      new Set(
        selectedItems.filter((i) => i.origin === "section").map((i) => i.id)
      ),
    [selectedItems]
  );

  const isItemSelected = useCallback(
    (item: FieldsetFormListItem): boolean =>
      item.origin === "section"
        ? selectedSectionIds.has(item.id)
        : selectedFieldNames.has(item.name),
    [selectedFieldNames, selectedSectionIds]
  );

  const matchesSearch = useCallback(
    (name: string): boolean =>
      !searchString || name.toLowerCase().includes(searchString.toLowerCase()),
    [searchString]
  );

  const allFieldItems = useMemo(
    () =>
      knownKeys.map(
        (key): FieldsetFormListItem => ({ name: key, origin: "data" })
      ),
    [knownKeys]
  );

  const allSectionItems = useMemo(
    () =>
      allowedSections.map(
        ({ name, id }): FieldsetFormListItem => ({
          name,
          id,
          origin: "section",
        })
      ),
    [allowedSections]
  );

  const visibleFields = useMemo(() => {
    const filtered = allFieldItems.filter((item) => matchesSearch(item.name));
    return showSelectedFields ? filtered.filter(isItemSelected) : filtered;
  }, [allFieldItems, matchesSearch, showSelectedFields, isItemSelected]);

  const visibleSections = useMemo(() => {
    const filtered = allSectionItems.filter((item) => matchesSearch(item.name));
    return showSelectedSections ? filtered.filter(isItemSelected) : filtered;
  }, [allSectionItems, matchesSearch, showSelectedSections, isItemSelected]);

  const onToggleItem = useCallback(
    (item: FieldsetFormListItem, next: boolean): void => {
      setMissingKeys(false);
      setSelectedItems((prev) => {
        const selected =
          item.origin === "section"
            ? prev.some((s) => s.origin === "section" && s.id === item.id)
            : prev.some((s) => s.origin === "data" && s.name === item.name);
        if (next && !selected) return [...prev, item];
        if (!next && selected) {
          if (item.origin === "section") {
            return prev.filter(
              (s) => !(s.origin === "section" && s.id === item.id)
            );
          }
          return prev.filter(
            (s) => !(s.origin === "data" && s.name === item.name)
          );
        }
        return prev;
      });
    },
    []
  );

  const addAll = (): void => {
    setMissingKeys(false);
    setSelectedItems((prev) => {
      const next = [...prev];
      visibleFields.forEach((item) => {
        const exists = next.some(
          (s) => s.origin === "data" && s.name === item.name
        );
        if (!exists) next.push(item);
      });
      visibleSections.forEach((item) => {
        const exists = next.some(
          (s) => s.origin === "section" && s.id === item.id
        );
        if (!exists) next.push(item);
      });
      return next;
    });
  };

  const removeAll = (): void => {
    const visibleFieldNames = new Set(visibleFields.map((f) => f.name));
    const visibleSectionIds = new Set(visibleSections.map((s) => s.id));
    setSelectedItems((prev) =>
      prev.filter(
        (item) =>
          !(item.origin === "data" && visibleFieldNames.has(item.name)) &&
          !(item.origin === "section" && visibleSectionIds.has(item.id))
      )
    );
  };

  const validateForm = (): boolean => {
    setMissingName(!fieldsetName);
    setMissingKeys(selectedItems.length < 1);
    return !!fieldsetName && selectedItems.length > 0;
  };

  const collectData = (): ModifiedFieldset => ({
    name: fieldsetName,
    fields: selectedItems.map((item) => {
      if (item.origin === "section" && item.id) {
        return { section_id: item.id };
      }
      return { key: item.name };
    }),
    use_as_default: useAsDefault,
  });

  const onCreateFieldset = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsCopying?.(false);
    await createFieldset(token, collectData())
      .then((fieldset) => setSelectedFieldset(fieldset))
      .finally(async () => {
        await refetchFieldsets();
      });
  };

  const onUpdateFieldset = async (): Promise<void> => {
    if (!validateForm()) return;
    await updateFieldset(token, selectedFieldset?.id, collectData())
      .then((fieldset) => setSelectedFieldset(fieldset))
      .finally(async () => {
        await refetchFieldsets();
      });
  };

  const onDeleteFieldset = async (): Promise<void> => {
    setMissingKeys(false);
    setMissingName(false);
    await deleteFieldset(token, selectedFieldset?.id)
      .then(() => setSelectedFieldset(undefined))
      .finally(async () => {
        await refetchFieldsets();
      });
  };

  const onSubmit = (): void => {
    if (!selectedFieldset || isCopying || isUnsavedDefaultFieldset) {
      onCreateFieldset();
      return;
    }
    onUpdateFieldset();
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-py-2">
      <div className="tw-flex tw-flex-col tw-gap-1">
        <label
          htmlFor="fieldset-name-input"
          className="tw-text-sm tw-font-medium tw-text-gray-700"
        >
          Name
        </label>
        <input
          id="fieldset-name-input"
          data-testid="fieldset-name-input"
          type="text"
          autoFocus
          placeholder="Input schema name"
          tabIndex={1}
          value={fieldsetName}
          disabled={useAsDefault}
          onChange={({ target }): void => {
            setMissingName(false);
            setFieldsetName(target.value);
          }}
          className={`tw-form-input tw-w-full tw-rounded-md tw-border-0 tw-p-2.5 tw-text-gray-900 tw-shadow-sm tw-ring-1 tw-ring-inset focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-gray-400 disabled:tw-text-gray-400 disabled:tw-ring-gray-100 ${
            missingName
              ? "tw-ring-red-400 focus:tw-ring-red-400"
              : "tw-ring-gray-300"
          }`}
        />
        {missingName && (
          <p
            className="tw-text-sm tw-text-red-500"
            data-testid="fieldset-name-error"
          >
            Name is required
          </p>
        )}
      </div>

      <hr className="tw-border-gray-200" />

      <SearchBar
        value={searchString}
        onValueChange={setSearchString}
        placeholder="Search Fields & Sections"
        data-testid="fieldset-form-search"
      />

      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <FieldsetFormList
          testIdPrefix="fieldset-available-fields"
          items={visibleFields}
          isItemSelected={isItemSelected}
          onToggle={onToggleItem}
          maxHeight={height}
          emptyMessage={
            showSelectedFields ? "No fields selected" : "No fields found"
          }
          header={
            <ColumnHeader
              heading="Available fields"
              selectedCount={selectedFieldNames.size}
              totalCount={allFieldItems.length}
              itemLabel="fields"
              showSelected={showSelectedFields}
              setShowSelected={setShowSelectedFields}
              showSelectedTestId="fieldset-show-selected-fields"
            />
          }
        />

        <FieldsetFormList
          testIdPrefix="fieldset-available-sections"
          items={visibleSections}
          isItemSelected={isItemSelected}
          onToggle={onToggleItem}
          maxHeight={height}
          emptyMessage={
            showSelectedSections ? "No sections selected" : "No sections found"
          }
          header={
            <ColumnHeader
              heading="Available sections"
              selectedCount={selectedSectionIds.size}
              totalCount={allSectionItems.length}
              itemLabel="sections"
              showSelected={showSelectedSections}
              setShowSelected={setShowSelectedSections}
              showSelectedTestId="fieldset-show-selected-sections"
            />
          }
        />
      </div>

      {missingKeys && (
        <p
          className="tw-text-sm tw-text-red-500"
          data-testid="fieldset-keys-error"
        >
          At least one selected Field or Section is required
        </p>
      )}

      <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <Button
          variant="secondary-alt"
          size="sm"
          compact
          onClick={addAll}
          data-testid="fieldset-add-all"
        >
          Add all
        </Button>
        <Button
          variant="secondary-alt"
          size="sm"
          compact
          onClick={removeAll}
          data-testid="fieldset-remove-all"
        >
          Remove all
        </Button>
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
          {selectedFieldset && !isUnsavedDefaultFieldset && (
            <Button
              variant="secondary-alt"
              size="sm"
              compact
              onClick={(): Promise<void> => onDeleteFieldset()}
              disabled={loading}
              data-testid="fieldset-delete"
            >
              Delete
            </Button>
          )}
          <Button
            data-testid="fieldset-submit-form"
            variant="primary"
            size="sm"
            compact
            onClick={onSubmit}
            disabled={loading}
          >
            {submitButtonName}
          </Button>
        </div>
      </div>
    </div>
  );
};

type ColumnHeaderProps = {
  heading: string;
  selectedCount: number;
  totalCount: number;
  itemLabel: string;
  showSelected: boolean;
  setShowSelected: (next: boolean) => void;
  showSelectedTestId: string;
};

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  heading,
  selectedCount,
  totalCount,
  itemLabel,
  showSelected,
  setShowSelected,
  showSelectedTestId,
}) => {
  const toggleId = `${showSelectedTestId}-checkbox`;
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <hr className="tw-border-gray-200 tw-m-0" />
      <div className="tw-flex tw-items-center tw-gap-3 tw-pl-1 tw-pr-2">
        <h5 className={`${HEADING_CLASSES} tw-m-0`}>
          {heading}{" "}
          <span className="tw-text-sm tw-text-gray-400 tw-font-normal">
            ({selectedCount} of {totalCount} {itemLabel} selected)
          </span>
        </h5>
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
          <span
            onClick={(): void => setShowSelected(!showSelected)}
            className="tw-cursor-pointer tw-select-none"
            role="presentation"
          >
            Show selected
          </span>
          <ToggleCheckbox
            checkboxId={toggleId}
            checked={showSelected}
            setChecked={setShowSelected}
            data-testid={showSelectedTestId}
          />
        </div>
      </div>
      <hr className="tw-border-gray-200 tw-m-0" />
    </div>
  );
};
